import "./styles.scss";
import {
  memo,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";

import { useApp } from "context/app";
import useResponsive from "libs/helpers/useResponsive";

import SelectablePill from "components/SelectablePill";
import TaskInfo from "components/TaskInfo";
import TextEditor, {
  convertToHTML,
  htmlToDraftBlocks,
} from "components/TextEditor";
import FilesUpload from "components/FilesUpload";
import Select from "components/form/Select";
import {
  StatusExplanation,
  PriorityExplanation,
} from "page_components/shared/tasks/explanations";
import {
  Webmasters,
  Tracker,
  Completion,
  ModalDescription,
} from "./components";

import { ReactComponent as EditIcon } from "assets/icons/edit.svg";

import task_status_data from "libs/task_status_data.json";
import task_priority_data from "libs/task_priority_data.json";

const mainClass = "task-view-main";

const Main = ({
  data,
  website,
  loading,
  file_progress,
  timeTracking,
  currentTimeTracking,
  updateTask,
  editMessage,
  isCompletelyDisabled,
}) => {
  const { t } = useTranslation();
  const { user } = useApp();
  const { max } = useResponsive();

  const [isDescriptionEditOpen, setIsDescriptionEditOpen] = useState(false);
  const [explanation, setExplanation] = useState(null);

  const actionsRef = useRef(null);
  const attachmentsRef = useRef(null);
  const [elementsHeight, setElementsHeight] = useState(null);

  const description = useMemo(() => data?.messages?.[0], [data?.messages]);

  const status_data = task_status_data?.find(
    (item) => item?.value === data?.status,
  );
  const priority_data = task_priority_data?.find(
    (item) => item?.value === data?.priority,
  );

  useLayoutEffect(() => {
    const handleElementsHeight = () => {
      const actions = actionsRef.current;
      const attachments = attachmentsRef.current;

      if (actions && attachments) {
        setElementsHeight({
          actions: actions.offsetHeight,
          attachments: attachments.offsetHeight,
        });
      }
    };

    handleElementsHeight();

    window.addEventListener("resize", handleElementsHeight);

    return () => {
      window.removeEventListener("resize", handleElementsHeight);
    };
  }, []);

  if (loading) return null;

  const isUploading = file_progress !== undefined && file_progress !== null;
  const filesInputId = `task-files--${data?._id}`;

  return (
    <>
      <div className={mainClass}>
        <div className={`${mainClass}__actions`} ref={actionsRef}>
          {max.sm ? (
            <>
              <div className="priority">
                <Select.Priority
                  labelCta={{
                    text: t("Learn more"),
                    onClick: () => setExplanation("priority"),
                  }}
                  selected={{
                    value: priority_data?.value,
                    label: t(priority_data?.label),
                    color: priority_data?.color,
                  }}
                  forceBottom
                  isCompletelyDisabled={isCompletelyDisabled}
                  onChange={
                    typeof updateTask === "function"
                      ? (option) => updateTask({ priority: option.value })
                      : undefined
                  }
                />
              </div>
              <div className="status">
                <Select.Status
                  labelCta={{
                    text: t("Learn more"),
                    onClick: () => setExplanation("status"),
                  }}
                  optionFilters={["canceled"]}
                  selected={{
                    value: status_data?.value,
                    label: t(status_data?.label),
                    color: status_data?.color,
                  }}
                  isCompletelyDisabled={isCompletelyDisabled}
                  onChange={
                    typeof updateTask === "function"
                      ? (option) => updateTask({ status: option.value })
                      : undefined
                  }
                />
              </div>
              <div className="work-time">
                <p className={`${mainClass}__label`}>{t("Work time")}</p>
                <Tracker
                  id={data?._id}
                  time_entries={data?.time_entries}
                  role={user?.type}
                  timeTracking={timeTracking}
                  isActive={currentTimeTracking?.tasks?.[0]?._id === data?._id}
                  isCompletelyDisabled={isCompletelyDisabled}
                />
              </div>
              <div className="due-date">
                <p className={`${mainClass}__label`}>{t("Completion date")}</p>
                <Completion
                  due_date={data?.due_date}
                  isClient={user?.type === "client"}
                  isCompletelyDisabled={isCompletelyDisabled}
                  onChange={
                    !isCompletelyDisabled &&
                    (user?.type === "admin" || user?.type === "webmaster")
                      ? (date) => updateTask({ due_date: date })
                      : undefined
                  }
                />
              </div>
              {user?.type === "admin" &&
                (isCompletelyDisabled ? data?.webmaster?.length > 0 : true) && (
                  <div className="webmasters">
                    <p className={`${mainClass}__label`}>{t("To")}</p>
                    <Webmasters
                      data={data?.webmaster}
                      isDisabled={isCompletelyDisabled}
                      onChange={(value) =>
                        updateTask({
                          webmaster: value.map((item) => item.id),
                        })
                      }
                    />
                  </div>
                )}
              <div className="created-at">
                <p>
                  {t("Created")}:{" "}
                  {data?.created_at
                    ? moment(data.created_at).format("LL")
                    : "-"}
                  {website?.name && (
                    <>
                      {" "}
                      {t("for")}{" "}
                      {website?.onClick ? (
                        <button type="button" onClick={website.onClick}>
                          {website.name}
                        </button>
                      ) : (
                        website.name
                      )}
                    </>
                  )}
                </p>
              </div>
            </>
          ) : (
            <>
              <div className="status">
                <p className={`${mainClass}__label`}>Status</p>
                <SelectablePill
                  data={{
                    value: status_data?.value,
                    label: t(status_data?.label),
                    color: status_data?.color,
                  }}
                  options={task_status_data
                    .filter((item) => item?.key !== "canceled")
                    .map((item) => ({
                      ...item,
                      label: t(item.label),
                    }))}
                  optionsFooter={
                    <TaskInfo onClick={() => setExplanation("status")} />
                  }
                  isDisabled={isUploading}
                  isCompletelyDisabled={isCompletelyDisabled}
                  onChange={
                    typeof updateTask === "function"
                      ? (option) => updateTask({ status: option.value })
                      : undefined
                  }
                />
              </div>
              <div className="priority">
                <p className={`${mainClass}__label`}>{t("Priority")}</p>
                <SelectablePill
                  data={{
                    value: priority_data?.value,
                    label: t(priority_data?.label),
                    color: priority_data?.color,
                  }}
                  icon="flag"
                  options={task_priority_data.map((item) => ({
                    ...item,
                    label: t(item.label),
                  }))}
                  optionsFooter={
                    <TaskInfo
                      type="priority"
                      onClick={() => setExplanation("priority")}
                    />
                  }
                  isDisabled={isUploading}
                  isCompletelyDisabled={isCompletelyDisabled}
                  onChange={
                    typeof updateTask === "function"
                      ? (option) => updateTask({ priority: option.value })
                      : undefined
                  }
                />
              </div>
              <div>
                <p className={`${mainClass}__label`}>{t("Work time")}</p>
                <Tracker
                  id={data?._id}
                  time_entries={data?.time_entries}
                  role={user?.type}
                  timeTracking={timeTracking}
                  isActive={currentTimeTracking?.tasks?.[0]?._id === data?._id}
                  isCompletelyDisabled={isCompletelyDisabled}
                />
              </div>
              <div>
                <p className={`${mainClass}__label`}>{t("Completion date")}</p>
                <Completion
                  due_date={data?.due_date}
                  isClient={user?.type === "client"}
                  isCompletelyDisabled={isCompletelyDisabled}
                  onChange={
                    !isCompletelyDisabled &&
                    (user?.type === "admin" || user?.type === "webmaster")
                      ? (date) => updateTask({ due_date: date })
                      : undefined
                  }
                />
              </div>
              {user?.type === "admin" &&
                (isCompletelyDisabled ? data?.webmaster?.length > 0 : true) && (
                  <div className={`${mainClass}__action-full`}>
                    <p className={`${mainClass}__label`}>{t("To")}</p>
                    <Webmasters
                      data={data?.webmaster}
                      isDisabled={isCompletelyDisabled}
                      onChange={(value) =>
                        updateTask({
                          webmaster: value.map((item) => item.id),
                        })
                      }
                    />
                  </div>
                )}
            </>
          )}
        </div>
        <div
          className={`${mainClass}__description`}
          style={
            elementsHeight && !max.sm
              ? {
                  "--height-offset": `${Object.values(elementsHeight).reduce(
                    (sum, height) => sum + height,
                    0,
                  )}px`,
                }
              : undefined
          }
        >
          <p className={`${mainClass}__label`}>
            {t("Description")}{" "}
            {max.sm && !isCompletelyDisabled && (
              <button
                type="button"
                onClick={() => setIsDescriptionEditOpen(true)}
              >
                <i>
                  <EditIcon />
                </i>
                {t("Edit")}
              </button>
            )}
          </p>
          {max.sm ? (
            <div>
              <div
                className={`${mainClass}__description-content`}
                dangerouslySetInnerHTML={{ __html: description?.text }}
              />
            </div>
          ) : (
            <MemoizedEditor
              id={description?._id}
              text={description?.text}
              attachmentInputId={filesInputId}
              isUploading={isUploading}
              isCompletelyDisabled={isCompletelyDisabled}
              editMessage={editMessage}
            />
          )}
        </div>
        {!(!(description?.files?.length > 0) && isCompletelyDisabled) && (
          <div className={`${mainClass}__attachments`} ref={attachmentsRef}>
            <FilesUpload
              layout={description?.files?.length > 0 ? "columns" : "compact"}
              label={t("Attachments")}
              description={t("Click or drag a file")}
              serverFiles={description?.files ?? []}
              progress={file_progress}
              inputId={filesInputId}
              isCompletelyDisabled={isCompletelyDisabled}
              onChange={(files) =>
                editMessage(description._id, "KEEP_SAME", files, [])
              }
              onRemove={(file) =>
                editMessage(description._id, "KEEP_SAME", [], [file.key])
              }
            />
          </div>
        )}
      </div>

      {max.sm && !isCompletelyDisabled && (
        <ModalDescription
          id={description?._id}
          text={description?.text}
          isUploading={isUploading}
          isOpen={isDescriptionEditOpen}
          editMessage={editMessage}
          onClose={() => setIsDescriptionEditOpen(false)}
        />
      )}
      <StatusExplanation
        isOpen={explanation === "status"}
        onClose={() => setExplanation(null)}
      />
      <PriorityExplanation
        isOpen={explanation === "priority"}
        onClose={() => setExplanation(null)}
      />
    </>
  );
};

const MemoizedEditor = memo(
  ({
    id,
    text,
    attachmentInputId,
    isUploading,
    isCompletelyDisabled,
    editMessage,
  }) => {
    const [content, setContent] = useState(null);
    const init = useRef(false);

    useLayoutEffect(() => {
      if (text && !init.current) {
        setContent(htmlToDraftBlocks(text));

        init.current = true;
      }
    }, [text]);

    const handleSubmit = useCallback(() => {
      if (id && typeof editMessage === "function")
        editMessage(id, convertToHTML(content), [], []);
    }, [id, content, editMessage]);

    return (
      <TextEditor
        id="description"
        content={content}
        setContent={setContent}
        attachmentInputId={attachmentInputId}
        autoSave
        isDisabled={isUploading}
        isCompletelyDisabled={isCompletelyDisabled}
        onSubmit={handleSubmit}
      />
    );
  },
);

export default Main;
